/**
 * Transcript Service
 * 
 * Main service for transcript operations
 * Coordinates between other services and provides high-level API
 */

import transcriptApi from '../../api/transcriptApi';
import BlockService from './BlockService';
import WordService from './WordService';
import LockService from './LockService';
import PlaybackService from './PlaybackService';

/**
 * Transcript Service Class
 */
class TranscriptService {
  /**
   * Fetch transcript segments in pages (for very long transcripts).
   * @param {string} sessionId
   * @param {number} offset
   * @param {number} limit
   */
  static async getTranscriptSegments(sessionId, offset = 0, limit = 500) {
    try {
      const response = await transcriptApi.getTranscriptSegments(sessionId, { offset, limit });
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to fetch transcript segments',
      };
    }
  }

  /**
   * Update transcript on backend
   * @param {string} sessionId - Session ID
   * @param {Array} blocks - Transcript blocks
   * @returns {Promise<Object>} Update response
   */
  static async updateTranscript(sessionId, blocks) {
    try {
      const response = await transcriptApi.updateTranscript(sessionId, {
        segments: blocks,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to update transcript',
      };
    }
  }

  /**
   * Save transcript as new version
   * @param {string} sessionId - Session ID
   * @param {Array} blocks - Transcript blocks
   * @param {string|null} description - Version description (optional)
   * @returns {Promise<Object>} Save response
   */
  static async saveVersion(sessionId, blocks, description = null) {
    try {
      const response = await transcriptApi.saveTranscriptVersion(sessionId, {
        segments: blocks,
      }, description);
      return {
        success: true,
        data: response,
        versionNumber: response.version_number,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to save version',
      };
    }
  }

  /**
   * Rename speaker across all blocks
   * @param {string} sessionId - Session ID
   * @param {string} oldName - Old speaker name
   * @param {string} newName - New speaker name
   * @returns {Promise<Object>} Rename response
   */
  static async renameSpeaker(sessionId, oldName, newName) {
    try {
      await transcriptApi.renameSpeaker(sessionId, oldName, newName);
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to rename speaker',
      };
    }
  }

  /**
   * Delete speaker and merge to another
   * @param {string} sessionId - Session ID
   * @param {string} speakerName - Speaker name to delete
   * @param {string} mergeToSpeaker - Speaker to merge to
   * @returns {Promise<Object>} Delete response
   */
  static async deleteSpeaker(sessionId, speakerName, mergeToSpeaker) {
    try {
      await transcriptApi.deleteSpeaker(sessionId, speakerName, mergeToSpeaker);
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete speaker',
      };
    }
  }

  // Re-export BlockService methods for convenience
  static splitBlock = BlockService.splitBlock;
  static mergeUp = BlockService.mergeUp;
  static mergeDown = BlockService.mergeDown;
  static deleteBlock = BlockService.delete;
  static duplicateBlock = BlockService.duplicate;
  static updateBlockText = BlockService.updateText;
  static updateBlockSpeaker = BlockService.updateSpeaker;
  static bulkUpdateSpeaker = BlockService.bulkUpdateSpeaker;
  static reorderBlocks = BlockService.reorder;
  static moveBlockUp = BlockService.moveUp;
  static moveBlockDown = BlockService.moveDown;
  static createEmptyBlock = BlockService.createEmptyBlock;

  // Re-export WordService methods
  static findMatchingWord = WordService.findMatchingWord;
  static hasWords = WordService.hasWords;
  static findActiveWordIndex = WordService.findActiveWordIndex;

  // Re-export LockService methods
  static getLockStatus = LockService.getLockStatus;
  static acquireLock = LockService.acquireLock;
  static releaseLock = LockService.releaseLock;
  static forceOverrideLock = LockService.forceOverrideLock;

  // Re-export PlaybackService methods
  static playFromSelection = PlaybackService.playFromSelection;
}

export default TranscriptService;

